import {groupBy, naturalSortByKey, objectFromEntries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {ArchiveStatus} from 'archive/ArchiveConstants';
import isArchived from 'archive/isArchived';
import DataConnector from 'connect/DataConnector';
import {FILTER_PARENT_STORABLE_ROOT} from 'constants/FileBrowserConstants';
import ModelTypeConstants, {SCHEMA_TO_STORABLE, StorableItemType} from 'constants/ModelTypeConstants';
import {StorableSummary, StorableSummaryExtras} from 'constants/SampleConstants';
import {PotentialChildStorable} from 'inventory/location-validation/ParentStorableValidationUtils';
import StorableSummaryExtrasManager from 'models/managers/StorableSummaryExtrasManager';
import BenchlingProfiler, {logProfilerTimingEvent} from 'performance/BenchlingProfiler';
import {
  LOCATION_PICKER_FETCH_FINISHED_EVENT,
  LOCATION_PICKER_FETCH_STARTED_EVENT,
  LOCATION_PICKER_RENDER_FINISHED_EVENT,
  LOCATION_PICKER_RENDER_STARTED_EVENT,
} from 'performance/processInventoryEvents';
import {fetchLocationSchemaIds} from 'samples/LocationDAO';
import * as StorableActions from 'samples/StorableActions';
import {
  graphQLStorableSummaryToREST,
  nullableRESTStorableSummaryToGraphQL,
} from 'samples/storables/graphql/translateStorable';
import {storableToSelectTreeItem} from 'samples/storables/ParentStorableUtil';
import StorableStore from 'samples/StorableStore';
import {FilterKey, Operator, SearchParams, SortKey} from 'search/SearchConstants';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import SelectTree, {LoadMorePromise} from 'ui-library/select-tree/SelectTree';
import {TreeItem} from 'ui-library/select-tree/SelectTreeConstants';
import APIUtil from 'util/APIUtil';
import {Deferred} from 'util/makeDeferred';

import {StorableSummaryGraphQL} from '../../../graphql-fragments/inventory/__generated/StorableSummaryGraphQL';

const FETCH_LIMIT = 100;

function storablesToSelectTreeItems(
  storables: Array<StorableSummary>,
  storablesToBeMoved?: Array<PotentialChildStorable> | null,
  extras?: StorableSummaryExtras,
  parentStorableTypes?: Array<StorableItemType>
): Array<TreeItem> {
  return storables.map((storable) =>
    storableToSelectTreeItem({storable, storablesToBeMoved, extras, parentStorableTypes})
  );
}

function fixedUpStorableTypes(storableTypes: Array<StorableItemType>): Array<StorableItemType> {
  return Array.from(new Set([ModelTypeConstants.LOCATION, ...storableTypes]));
}

class ParentStorableSelectTreeConnector extends DataConnector<
  ParentStorableSelectTreeProps,
  EnhancedParentStorableSelectTreeProps
> {
  override shouldSubscribeToRedux = false;
  override stores = [StorableStore];

  enhanceProps(): EnhancedParentStorableSelectTreeProps {
    let selectTreeItems: Array<TreeItem>;
    let selectTreeBaseLeafItems: Array<TreeItem>;
    let shouldLoadRootItems: boolean;

    const storableTypes = fixedUpStorableTypes(this.props.storableTypes);

    if (this.props.selectTreeItems == null) {
      // If `selectTreeItems` are not supplied, get them from the redux store.
      shouldLoadRootItems = true;
      const storables: {[storableId: string]: StorableSummary} = this.storableStore().getStorableSummaries(
        this.props.registryId!,
        storableTypes
      );

      // Include ancestral storables that are not present in StorableStore.storableSummaries
      const otherAncestralStorables: {[storableId: string]: StorableSummary} = objectFromEntries(
        Object.values(this.storableStore().getStorableSummaryExtras().ancestralStorablesById ?? {})
          .flat()
          .filter(
            (otherAncestralStorable) =>
              !storables.hasOwnProperty(otherAncestralStorable.id) && // avoid duplicates
              storableTypes.includes(
                APIUtil.apiIdToType(otherAncestralStorable.id) as unknown as StorableItemType
              )
          )
          .map((otherAncestralStorable) => [otherAncestralStorable.id, otherAncestralStorable])
      );
      const filteredStorables = [
        ...Object.values(storables),
        ...Object.values(otherAncestralStorables),
      ].filter((storable) => this.props.includeArchived || !isArchived(storable));

      // Some storable summaries may have made it into the redux store some other way, and storable summary
      // extras may not have been fetched for them. Queue a bulk fetch of any that are missing.
      StorableSummaryExtrasManager.requestFetchIfMissing(filteredStorables.map((storable) => storable.id));

      const groupedItems = groupBy(filteredStorables, ({id}) =>
        APIUtil.isLocation(id) ? 'nonLeafItems' : 'leafItems'
      );
      const {
        nonLeafItems,
        leafItems,
      }: {nonLeafItems: Array<StorableSummary>; leafItems: Array<StorableSummary>} = {
        nonLeafItems: [],
        leafItems: [],
        ...groupedItems,
      };

      // [Potentially] non-leaf items (locations) are passed to `SelectTree` as `items`; non-locations are
      // passed as `baseLeafItems`.
      selectTreeItems = storablesToSelectTreeItems(
        naturalSortByKey(nonLeafItems, 'name'),
        this.props.storablesToBeMoved,
        this.storableStore().getStorableSummaryExtras(),
        this.props.storableTypes
      );

      const rootLevelLeafItems = leafItems.filter((storable) => storable.parentStorableId == null);
      selectTreeBaseLeafItems = storablesToSelectTreeItems(
        rootLevelLeafItems, // no need to sort here, these are sorted in `getBaseLeafItems()` below
        this.props.storablesToBeMoved,
        this.storableStore().getStorableSummaryExtras(),
        this.props.storableTypes
      );
    } else {
      // When `selectTreeItems` are supplied as a prop, extras may not have been loaded for all of them.
      StorableSummaryExtrasManager.requestFetchIfMissing(this.props.selectTreeItems.map(({id}) => id));

      selectTreeItems = this.props.selectTreeItems;
      selectTreeBaseLeafItems = [];
      shouldLoadRootItems = false;
    }

    // Gather info used to determine whether an item in the tree has children and should be expandable
    const numChildrenByStorableId = objectFromEntries(
      Object.entries(
        this.storableStore().getStorableSummaryExtras().childStorableSchemasByLocationId ?? {}
      ).map(([locationId, schemasAndCounts]) => [
        locationId,
        schemasAndCounts
          .filter((s) =>
            storableTypes
              .map((storableItem) =>
                [ModelTypeConstants.FIXED_PLATE, ModelTypeConstants.MATRIX_PLATE].includes(storableItem)
                  ? ModelTypeConstants.PLATE // We don't have plate schema type here, so we make a best effort
                  : storableItem
              )
              .includes(SCHEMA_TO_STORABLE[APIUtil.apiIdToType(s.childStorableSchema.id)]!)
          )
          .map(({numChildStorables}) => numChildStorables)
          .reduce((previousValue, currentValue) => previousValue + currentValue, 0),
      ])
    );

    return {selectTreeItems, selectTreeBaseLeafItems, numChildrenByStorableId, shouldLoadRootItems};
  }
}

function createSearchParams({
  registryId,
  storableTypes,
  parentStorableId,
  offset,
  includeArchived = true,
  query = '',
  storableSchemaId,
  locationSchemaIds,
}: {
  registryId: string;
  storableTypes: Array<string>;
  parentStorableId: string;
  offset: number;
  includeArchived?: boolean;
  query?: string;
  storableSchemaId?: string;
  locationSchemaIds: Array<string> | null;
}): SearchParams {
  return {
    offset,
    query,
    limit: FETCH_LIMIT,
    filters: [
      {key: FilterKey.REGISTRY, operator: Operator.IS, value: registryId},
      {key: FilterKey.TYPES, operator: Operator.IS_ONE_OF, value: storableTypes},
      {key: FilterKey.PARENT_STORABLE, operator: Operator.IS_ONE_OF, value: parentStorableId},
      ...(includeArchived
        ? []
        : [
            {
              key: FilterKey.ARCHIVE_PURPOSES as const,
              operator: Operator.IS_ONE_OF,
              value: [ArchiveStatus.NOT_ARCHIVED],
            },
          ]),
      ...(storableSchemaId != null && locationSchemaIds != null
        ? [
            {
              key: FilterKey.SCHEMA as const,
              operator: Operator.IS_ONE_OF,
              // We search for locations as well as storables of schema `storableSchemaId`, because we need
              // locations that may be ancestors of storables of interest to show up in the tree. Otherwise it
              // wouldn't be possible to navigate to those storables. We include locations in the search by
              // passing all location schema IDs here, because there is no other way to search for "any
              // location OR an item of schema <id>".
              // See https://benchling.slack.com/archives/C01G8C7DYA1/p1698774646655639
              value: [...locationSchemaIds, storableSchemaId],
            },
          ]
        : []),
    ],
    sorts: [{key: SortKey.SORT_NAME_KEY, reverse: false}],
  };
}

export interface ParentStorableSelectTreeProps {
  onSelectStorable: (storable: StorableSummaryGraphQL | null) => void;
  registryId?: string;
  includeArchived: boolean;
  selectedStorable?: StorableSummaryGraphQL | null;
  storableSchemaId?: string;
  storableTypes: Array<StorableItemType>;
  baseLeafItems?: Array<TreeItem>;
  shouldNotLoadMoreOnMount?: boolean;
  allowNone?: boolean;

  borderless?: boolean;

  // If supplied to ConnectedParentStorableSelectTree, this is passed through to ParentStorableSelectTree
  selectTreeItems?: Array<TreeItem>;

  storablesToBeMoved?: Array<PotentialChildStorable> | null;
}

interface EnhancedParentStorableSelectTreeProps {
  selectTreeItems: Array<TreeItem>;
  selectTreeBaseLeafItems: Array<TreeItem>;
  numChildrenByStorableId: {[key: string]: number};
  // When `selectTreeItems` are supplied upstream, we don't load root items on mount. We also don't allow
  // loading more root items. This is not for any product reason, but because the implementation would
  // require some refactoring: when `selectTreeItems` are _not_ supplied, all storables in the store are
  // rendered in the picker. The loading of root items depends on this behavior, so it doesn't work when
  // `selectTreeItems` _are_ supplied.
  shouldLoadRootItems: boolean;
}

interface ParentStorableSelectTreeState {
  rootOffset: number;
  rootHasMore: boolean;
  isLoadingRootItems: boolean;
  locationSchemaIds: Array<string> | null;
}

export class ParentStorableSelectTree extends React.Component<
  ParentStorableSelectTreeProps & EnhancedParentStorableSelectTreeProps,
  ParentStorableSelectTreeState
> {
  static defaultProps: Partial<ParentStorableSelectTreeProps> = {
    includeArchived: false,
  };
  static cancelFetchPromises: Map<string, Deferred<string> | null> = new Map();
  isUnmounted = false;

  constructor(props: Readonly<ParentStorableSelectTreeProps & EnhancedParentStorableSelectTreeProps>) {
    super(props);
    this.state = {rootOffset: 0, rootHasMore: true, isLoadingRootItems: false, locationSchemaIds: null};
  }

  async loadLocationSchemaIds(): Promise<void> {
    const locationSchemaIds = await fetchLocationSchemaIds();
    this.setState({locationSchemaIds});
  }

  override componentDidMount(): void {
    if (this.props.storableSchemaId != null) {
      // Once location schema IDs load, setState will trigger componentDidUpdate, which will load root items
      // if needed
      this.loadLocationSchemaIds();
    } else if (this.props.registryId != null && this.props.shouldLoadRootItems) {
      this.loadMoreRootItems(0);
    }
  }

  override componentDidUpdate(
    prevProps: Readonly<ParentStorableSelectTreeProps & EnhancedParentStorableSelectTreeProps>,
    prevState: ParentStorableSelectTreeState
  ): void {
    if (this.props.storableSchemaId !== prevProps.storableSchemaId) {
      if (this.props.storableSchemaId != null) {
        this.loadLocationSchemaIds(); // once they load, setState will trigger another componentDidUpdate
      } else {
        this.setState({locationSchemaIds: null});
      }
    }

    if (
      this.props.shouldLoadRootItems &&
      this.props.registryId != null &&
      (this.props.registryId !== prevProps.registryId ||
        this.state.locationSchemaIds !== prevState.locationSchemaIds)
    ) {
      this.loadMoreRootItems(0);
    }
  }

  override componentWillUnmount(): void {
    this.isUnmounted = true;
  }

  private loadMoreRootItems = (offset: number): LoadMorePromise => {
    this.setState({isLoadingRootItems: true});
    return this.loadMore(FILTER_PARENT_STORABLE_ROOT, offset)
      .then((result) => {
        if (result) {
          this.setState({rootHasMore: result.more, rootOffset: result.offset + result.items.length});
        }
        return result;
      })
      .finally(() => {
        if (!this.isUnmounted) {
          this.setState({isLoadingRootItems: false});
        }
      });
  };

  private loadMore = (storableId: string, offset: number): LoadMorePromise => {
    const {registryId} = this.props;

    logProfilerTimingEvent(LOCATION_PICKER_FETCH_STARTED_EVENT, {
      function: 'loadMore',
    });

    const cancelPromiseKey = `${registryId!}-${storableId}-${offset}`;
    ParentStorableSelectTree.cancelFetchPromises.get(cancelPromiseKey)?.resolve('ABORTED');

    const {responsePromise, cancelPromise} = StorableActions.searchStorablesWithSummaries(
      createSearchParams({
        registryId: registryId!,
        storableTypes: fixedUpStorableTypes(this.props.storableTypes),
        parentStorableId: storableId,
        includeArchived: this.props.includeArchived,
        offset,
        storableSchemaId: this.props.storableSchemaId,
        locationSchemaIds: this.state.locationSchemaIds,
      })
    );
    ParentStorableSelectTree.cancelFetchPromises.set(cancelPromiseKey, cancelPromise);

    return responsePromise.then((response) => {
      const {storableSummaries, storableSummaryExtras, total, error} = response;
      if (error) {
        return undefined;
      }
      logProfilerTimingEvent(LOCATION_PICKER_FETCH_FINISHED_EVENT, {
        fetchLimit: FETCH_LIMIT,
        numItemsFetched: storableSummaries.length,
        function: 'loadMore',
      });
      return {
        items: storablesToSelectTreeItems(
          storableSummaries,
          this.props.storablesToBeMoved,
          storableSummaryExtras,
          this.props.storableTypes
        ),
        more: offset + storableSummaries.length < total,
        offset,
      };
    });
  };

  private handleOnSelectStorableTree = (storableIds: Array<string>): void => {
    const [storableId] = storableIds;
    this.props.onSelectStorable(
      nullableRESTStorableSummaryToGraphQL(StorableStore.getStorableSummary(storableId))
    );
  };

  private isStorableExpandable = (item: TreeItem): boolean =>
    (APIUtil.apiIdToType(item.id) as string) === ModelTypeConstants.LOCATION &&
    this.props.numChildrenByStorableId[item.id] != null &&
    this.props.numChildrenByStorableId[item.id] > 0;

  // Base leaf items are displayed on the top of leaf items until loaded in by loadMore
  private getBaseLeafItems(): Array<TreeItem> {
    const {selectedStorable} = this.props;
    const selectedLeafItems: Array<TreeItem> =
      selectedStorable && !APIUtil.isLocation(selectedStorable.id)
        ? storablesToSelectTreeItems(
            [graphQLStorableSummaryToREST(selectedStorable)],
            this.props.storablesToBeMoved,
            StorableStore.getStorableSummaryExtras(),
            this.props.storableTypes
          )
        : [];
    const selectTreeBaseLeafItemIds = new Set(this.props.selectTreeBaseLeafItems.map((i) => i.id));

    return naturalSortByKey(
      [
        ...this.props.selectTreeBaseLeafItems,
        ...(this.props.baseLeafItems || selectedLeafItems).filter(
          (item) => !selectTreeBaseLeafItemIds.has(item.id)
        ),
      ],
      'name'
    );
  }

  // Storables should be sorted based on whether they are disabled _only_ if they are not a location. Locations
  // should stay in a consistent order so they can be used to navigate the hierarchy.
  private shouldDeprioritizeStorable = (item: TreeItem): boolean =>
    (APIUtil.apiIdToType(item.id) as string) !== ModelTypeConstants.LOCATION && (item.disabled ?? false);

  // This is used to sort items at the root level _within_ the separate groups of locations (non-leaf items)
  // and non-locations (leaf items); these items are already sorted by name. forceToTop is set based on
  // whether the storable is starred. See usage in SelectTree.getStateWithNewItemsAdded.
  // Locations and non-locations are sorted separately.
  // This applies the following sorts, in descending order of precedence:
  //    - forceToTop (starred) higher than not-starred
  //    - enabled non-locations higher than disabled ones (no-op for locations)
  private sortRootItems = (items: Array<TreeItem>): Array<TreeItem> =>
    items.sort(
      (item1, item2) =>
        (item1.forceToTop ? 0 : 1) - (item2.forceToTop ? 0 : 1) ||
        Number(this.shouldDeprioritizeStorable(item1)) - Number(this.shouldDeprioritizeStorable(item2))
    );

  // This is used to sort items not at the root level _within_ the separate groups of locations (non-leaf items)
  // and non-locations (leaf items). These items start with the sort order from the backend search response, which
  // also applies a starred search, then may be further sorted by `itemsSortFn`.
  // This applies the following sort:
  //    - enabled non-locations higher than disabled ones (no-op for locations)
  private sortItems = (items: Array<TreeItem>): Array<TreeItem> =>
    items.sort(
      (item1, item2) =>
        Number(this.shouldDeprioritizeStorable(item1)) - Number(this.shouldDeprioritizeStorable(item2))
    );

  override render(): JSX.Element | null {
    const baseLeafItems = this.getBaseLeafItems();

    if (this.props.registryId == null) {
      return null;
    } else if (
      (this.props.selectTreeItems.length === 0 && baseLeafItems.length === 0) ||
      (this.props.storableSchemaId != null && this.state.locationSchemaIds == null)
    ) {
      return (
        <div className="u-small-padding-bottom u-small-padding-top text-center">
          <HorizontalThrobber />
        </div>
      );
    }

    const normalizedStorableTypes = this.props.storableTypes.map((t) =>
      [ModelTypeConstants.MATRIX_PLATE, ModelTypeConstants.FIXED_PLATE].includes(t)
        ? ModelTypeConstants.PLATE
        : t
    );

    logProfilerTimingEvent(LOCATION_PICKER_RENDER_STARTED_EVENT, {
      numItemsToRender: this.props.selectTreeItems.length,
    });

    return (
      <BenchlingProfiler
        {...LOCATION_PICKER_RENDER_FINISHED_EVENT}
        extra={{
          numItemsRendered: this.props.selectTreeItems.length,
          hasSelectedItem: Boolean(this.props.selectedStorable),
        }}
      >
        <SelectTree
          allowNone={this.props.allowNone}
          baseLeafItems={baseLeafItems}
          className={classNames({
            'selectTree--borderless': this.props.borderless,
            [css(styles.maxHeight45vh)]: true,
          })}
          loadMore={this.loadMore}
          items={this.props.selectTreeItems}
          itemTypeName="locations"
          multiple={false}
          selectedIds={this.props.selectedStorable ? [this.props.selectedStorable.id] : []}
          onChangeSelectedIds={this.handleOnSelectStorableTree}
          shouldExpandNewItems={false}
          shouldExpandOnSelect={true}
          shouldNotLoadMoreOnMount={this.props.shouldNotLoadMoreOnMount}
          isLoadedItemExpandable={this.isStorableExpandable}
          rootItemSortFn={this.sortRootItems}
          itemSortFn={this.sortItems}
          onLoadMoreRootItems={() => {
            this.loadMoreRootItems(this.state.rootOffset);
          }}
          moreRootItems={this.props.shouldLoadRootItems && this.state.rootHasMore}
          isLoadingRootItems={this.state.isLoadingRootItems}
          headers={(this.props.storablesToBeMoved?.length ?? 0) > 0 ? ['Location', 'Available space'] : null}
          isItemSelectable={({id}) =>
            normalizedStorableTypes.includes(APIUtil.apiIdToMaybeType(id) as unknown as StorableItemType)
          }
        />
      </BenchlingProfiler>
    );
  }
}

export const ConnectedParentStorableSelectTree =
  ParentStorableSelectTreeConnector.connect(ParentStorableSelectTree);

const styles = StyleSheet.create({
  maxHeight45vh: {
    maxHeight: '45vh',
  },
});
